/**
 * Krok 1: Podstawy hodowli (nazwa + rasy + lokalizacja)
 * Numerowane pod-pytania jak na mockupie.
 */
import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Check, X, CheckCircle2, MapPin, ShieldCheck } from 'lucide-react';
import { DOG_BREEDS } from '@/data/breeds';
import { formatPostalCode, getVoivodeshipFromPostalCode } from '@/utils/postalCode';

const VOIVODESHIPS = [
  'dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie',
  'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie',
  'podkarpackie', 'podlaskie', 'pomorskie', 'śląskie',
  'świętokrzyskie', 'warmińsko-mazurskie', 'wielkopolskie', 'zachodniopomorskie',
];

// Związki / stowarzyszenia kynologiczne (zgodne z legacy KennelWizard dla spójności).
const ORGANIZATIONS = [
  { id: 'zkwp_fci', label: 'ZKwP (FCI)', fci: true },
  { id: 'fci_other', label: 'Inna organizacja FCI', fci: true },
  { id: 'khron', label: 'KHRON', fci: false },
  { id: 'wush', label: 'WUSH', fci: false },
  { id: 'other', label: 'Inna organizacja', fci: false },
  { id: 'unknown', label: 'Brak / nie podano', fci: false },
];

const NAME_MIN = 3;
const NAME_MAX = 60;

export default function Step1Basics({ data, updateData, errors = {} }) {
  const [breedQuery, setBreedQuery] = useState('');
  const selected = data.breeds || [];

  const filteredBreeds = useMemo(() => {
    // Autocomplete: podpowiedzi pokazujemy DOPIERO po wpisaniu zapytania.
    // Przy pełnej bazie FCI wyświetlanie wszystkich ras nie jest skalowalne.
    if (!breedQuery.trim()) return [];
    const q = breedQuery.toLowerCase();
    return DOG_BREEDS.filter((b) => b.toLowerCase().includes(q)).slice(0, 8);
  }, [breedQuery]);

  const toggleBreed = (breed) => {
    if (selected.includes(breed)) {
      updateData({ breeds: selected.filter((b) => b !== breed) });
    } else {
      updateData({ breeds: [...selected, breed], species: data.species?.length ? data.species : ['dog'] });
      setBreedQuery(''); // po wyborze czyścimy zapytanie → lista podpowiedzi się zwija
    }
  };

  const handlePostal = (val) => {
    const formatted = formatPostalCode(val);
    const updates = { postal_code: formatted };
    if (formatted && /^\d{2}-\d{3}$/.test(formatted) && !data.voivodeship) {
      const detected = getVoivodeshipFromPostalCode(formatted);
      if (detected) updates.voivodeship = detected;
    }
    updateData(updates);
  };

  const nameLen = (data.name || '').length;
  const nameValid = nameLen >= NAME_MIN && nameLen <= NAME_MAX;

  return (
    <div className="space-y-8" data-testid="wizard-v2-step-basics">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            Podstawy hodowli
          </h2>
        </div>
        <p className="text-gray-500 text-sm">
          Nazwa, rasy i lokalizacja — wystarczy do startu.
        </p>
      </header>

      {/* 1. Nazwa */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="kennel-name" className="text-sm font-semibold text-gray-900">
            1. Jak nazywa się Twoja hodowla?
          </Label>
          <span className={`text-xs font-medium ${nameValid ? 'text-emerald-600' : 'text-gray-400'}`}>
            {nameLen}/{NAME_MAX} {nameValid && <Check className="inline w-3.5 h-3.5 ml-0.5" />}
          </span>
        </div>
        <Input
          id="kennel-name"
          value={data.name || ''}
          onChange={(e) => updateData({ name: e.target.value })}
          placeholder="np. Złote Marzenia FCI"
          data-testid="wizard-v2-name-input"
          maxLength={NAME_MAX}
          className="h-12 text-base focus-visible:ring-orange-400"
        />
        {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
        <p className="text-xs text-gray-500">
          To będzie nazwa widoczna w Google, katalogach i na Twojej stronie.
        </p>
      </section>

      {/* 2. Rasy */}
      <section className="space-y-3">
        <Label className="text-sm font-semibold text-gray-900">
          2. Jakie rasy hodujesz? <span className="text-gray-400 font-normal">(możesz wybrać kilka)</span>
        </Label>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-2.5 rounded-lg bg-orange-50/60 border border-orange-100">
            {selected.map((breed) => (
              <button
                key={breed}
                type="button"
                onClick={() => toggleBreed(breed)}
                data-testid={`wizard-v2-selected-breed-${breed}`}
                className="px-2.5 py-1 bg-orange-500 text-white text-xs rounded-full flex items-center gap-1.5 hover:bg-orange-600"
              >
                {breed} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            value={breedQuery}
            onChange={(e) => setBreedQuery(e.target.value)}
            placeholder="Zacznij pisać, np. „Owczarek…”"
            data-testid="wizard-v2-breed-search"
            className="pl-9 h-11 focus-visible:ring-orange-400"
          />
        </div>

        {/* Autocomplete: lista podpowiedzi pojawia się dopiero po wpisaniu zapytania */}
        {breedQuery.trim() ? (
          filteredBreeds.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="wizard-v2-breed-suggestions">
              {filteredBreeds.map((breed) => {
                const isSelected = selected.includes(breed);
                return (
                  <button
                    key={breed}
                    type="button"
                    onClick={() => toggleBreed(breed)}
                    data-testid={`wizard-v2-breed-option-${breed}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all text-left ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50 text-orange-900 font-medium'
                        : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/30'
                    }`}
                  >
                    <span className="line-clamp-1">{breed}</span>
                    {isSelected && <Check className="w-4 h-4 text-orange-500 flex-shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 px-1 py-2" data-testid="wizard-v2-breed-noresult">
              Brak rasy „{breedQuery}". Sprawdź pisownię lub wpisz inną nazwę.
            </p>
          )
        ) : (
          <p className="text-xs text-gray-400 px-1">
            Zacznij pisać, aby wyszukać rasę z bazy FCI i dodać ją do hodowli.
          </p>
        )}
      </section>

      {/* 3. Lokalizacja */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-orange-500" />
          <Label className="text-sm font-semibold text-gray-900">3. Gdzie znajduje się hodowla?</Label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="kennel-postal" className="text-xs text-gray-600">Kod pocztowy</Label>
            <Input
              id="kennel-postal"
              value={data.postal_code || ''}
              onChange={(e) => handlePostal(e.target.value)}
              placeholder="00-000"
              maxLength={6}
              data-testid="wizard-v2-postal-input"
              className="h-11 focus-visible:ring-orange-400"
            />
            {errors.postal_code && <p className="text-xs text-red-600">{errors.postal_code}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kennel-city" className="text-xs text-gray-600">Miasto</Label>
            <Input
              id="kennel-city"
              value={data.city || ''}
              onChange={(e) => updateData({ city: e.target.value })}
              placeholder="np. Warszawa"
              data-testid="wizard-v2-city-input"
              className="h-11 focus-visible:ring-orange-400"
            />
            {errors.city && <p className="text-xs text-red-600">{errors.city}</p>}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="kennel-voiv" className="text-xs text-gray-600">Województwo</Label>
            <Select value={data.voivodeship || ''} onValueChange={(v) => updateData({ voivodeship: v })}>
              <SelectTrigger id="kennel-voiv" className="h-11" data-testid="wizard-v2-voivodeship-select">
                <SelectValue placeholder="Wybierz województwo…" />
              </SelectTrigger>
              <SelectContent>
                {VOIVODESHIPS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.voivodeship && <p className="text-xs text-red-600">{errors.voivodeship}</p>}
          </div>
        </div>
      </section>

      {/* 4. Związek / stowarzyszenie kynologiczne */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-orange-500" />
          <Label className="text-sm font-semibold text-gray-900">
            4. Do jakiego związku / stowarzyszenia należysz? <span className="text-red-500">*</span>
          </Label>
        </div>

        <Select
          value={data.organization_type || ''}
          onValueChange={(v) => {
            const org = ORGANIZATIONS.find((o) => o.id === v);
            updateData({
              organization_type: v,
              organization: org?.label || '',
              is_fci: !!org?.fci,
              ...(v !== 'other' ? { organization_custom: '' } : {}),
            });
          }}
        >
          <SelectTrigger className="h-11" data-testid="wizard-v2-organization-select">
            <SelectValue placeholder="Wybierz związek…" />
          </SelectTrigger>
          <SelectContent>
            {ORGANIZATIONS.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                <span className="flex items-center gap-2">
                  {org.label}
                  {org.fci && (
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">FCI</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.organization_type && <p className="text-xs text-red-600" data-testid="wizard-v2-organization-error">{errors.organization_type}</p>}
        {data.is_fci && (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid="wizard-v2-fci-badge">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">Hodowla zarejestrowana w FCI — to ważny znak zaufania dla kupujących.</span>
          </div>
        )}

        {/* Pole własne dla „Inna organizacja" */}
        {data.organization_type === 'other' && (
          <Input
            value={data.organization_custom || ''}
            onChange={(e) => updateData({ organization_custom: e.target.value })}
            placeholder="Podaj nazwę związku / stowarzyszenia"
            data-testid="wizard-v2-organization-custom"
            className="h-11 focus-visible:ring-orange-400"
          />
        )}
        {errors.organization_custom && <p className="text-xs text-red-600" data-testid="wizard-v2-organization-custom-error">{errors.organization_custom}</p>}
        <p className="text-xs text-gray-500">
          Przynależność do związku zwiększa wiarygodność hodowli w katalogu i wyszukiwarce.
        </p>
      </section>
    </div>
  );
}
